import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import CustomButton from "@/components/common/Button";
import { ProColumns } from "@ant-design/pro-components";
import { Edit2 } from "lucide-react";

import { BaseTable } from "@/components/BaseTable";
import MMNavigationRoutes from "../../utils/navigationRoutes";
import { OrganizationService } from "@/services/APIs";
import { Organization } from "@/utils/types/organization";

const ChapterProfileListPage = () => {
  const navigate = useNavigate();
  // const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(searchParams.get("pageSize") ?? "10", 10);
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);
  const [searchText, setSearchText] = useState("");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["organizations", page, pageSize],
    queryFn: () => OrganizationService.getAllOrganizations({ page, pageSize }),
  });

  // const handleDelete = async (memberId: string) => {
  //   try {
  //     await MemberService.deleteMember(memberId);
  //     queryClient.invalidateQueries({ queryKey: ["members"] });
  //     notify.success("Member deleted successfully");
  //   } catch (error: any) {
  //     console.error("Failed to delete member:", error);
  //     notify.error("Failed to delete member");
  //   }
  // };

  const handleEdit = (record: Organization) => {
    const orgId = record.org_id || record.id ? record.org_id : record.id;
    if (!orgId) {
      console.error("Organization ID is missing");
      return;
    }
    console.log("Organization ID:", orgId);
    navigate(`/mm${MMNavigationRoutes.EDIT_CHAPTER_PROFILE(orgId)}`);
  };

  const total = data?.count ?? 0;

  // Filter data based on search text
  const filteredData = useMemo(() => {
    const gridData = data?.results ?? [];
    if (!searchText.trim()) {
      return gridData;
    }
    const searchLower = searchText.toLowerCase();
    return gridData.filter((org: Organization) => {
      const orgName = (org.org_name ?? "").toLowerCase();
      const orgType = (
        org.organizationtype_name ??
        org.organizationtype ??
        ""
      ).toLowerCase();
      const email = (org.primaryemail ?? "").toLowerCase();

      return (
        orgName.includes(searchLower) ||
        orgType.includes(searchLower) ||
        email.includes(searchLower)
      );
    });
  }, [data?.results, searchText]);

  const columns: ProColumns<Organization>[] = [
    {
      title: "Organization Name",
      dataIndex: "org_name",
      key: "org_name",
      render: (text: any) => text ?? "-",
    },
    {
      title: "Organization Type",
      dataIndex: "organizationtype_name",
      key: "organizationtype_name",
      render: (_text: any, record: Organization) => {
        return record.organizationtype_name ?? record.organizationtype ?? "-";
      },
    },
    {
      title: "Member Count",
      dataIndex: "member_count",
      key: "member_count",
      render: (text: any) => text ?? 0,
    },
    {
      title: "Event Count",
      dataIndex: "event_count",
      key: "event_count",
      render: (text: any) => text ?? 0,
    },
    {
      title: "Primary Email",
      dataIndex: "primaryemail",
      key: "primaryemail",
      render: (text: any) => text ?? "-",
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() => handleEdit(record)}
          type="text"
          title="Edit Organization"
        />,
      ],
    },
    // {
    //   title: "Actions",
    //   width: 100,
    //   valueType: "option",
    //   render: (_, record) => [
    //     <CustomButton
    //       key="delete"
    //       icon={<Trash size={16} />}
    //       onClick={() => handleDelete(record.org_id)}
    //       type="text"
    //       title="Delete Organization"
    //     />,
    //   ],
    // },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Search Bar Section */}
      <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
        <div className="p-6 pt-6">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-search absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pl-10 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                placeholder="Search organizations..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />
            </div>
            <button
              className="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
              onClick={() => {
                // Add filter functionality here if needed
                console.log("Filters clicked");
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-filter mr-2 h-4 w-4"
              >
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              Filters
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <BaseTable<Organization>
        loading={isLoading}
        columns={columns}
        dataSource={filteredData}
        page={page}
        pageSize={pageSize}
        total={searchText.trim() ? filteredData.length : total}
        rowKey="org_id"
        toolBarRender={() => []}
        headerTitle="Chapter Profile List"
        onPageChange={(page, size) => {
          setPage(page);
          setPageSize(size ?? 10);
          searchParams.set("page", page.toString());
          searchParams.set("pageSize", size.toString());
          navigate(
            `/mm${MMNavigationRoutes.CHAPTER_PROFILE_LIST}?${searchParams.toString()}`
          );
        }}
        request={() => refetch()}
      />
    </div>
  );
};

export default ChapterProfileListPage;
